import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Shield, Sparkles, Award } from 'lucide-react';
import { companyInfo, products, applications } from '../mock';

const HomePage = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const heroImages = [
    "https://images.unsplash.com/photo-1654105727849-1b9d39357f16",
    "https://images.unsplash.com/photo-1677253200111-97d0d490d60e",
    "https://images.unsplash.com/photo-1690944926899-cc0a52be0730"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[85vh] sm:h-screen flex items-center justify-center overflow-hidden">
        {heroImages.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt="Architectural Glass"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-slate-900/50"></div>
          </div>
        ))}

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-rajdhani text-3xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
            Discover The Excellence Of<br className="hidden sm:block" /> Toughened Glass
          </h1>
          <p className="font-roboto text-sm sm:text-lg text-white/80 max-w-2xl mx-auto mb-8 sm:mb-10 font-light px-4">
            {companyInfo.experience} of premium architectural and safety glass manufacturing,
            based in Jaipur, India.
          </p>
          <Link
            to="/contact"
            data-testid="hero-cta"
            className="inline-flex items-center gap-3 bg-brand hover:bg-brand-dark text-white font-rajdhani font-semibold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 transition-colors"
          >
            GET A QUOTE
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* About Intro Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1654105727849-1b9d39357f16"
                alt="Manufacturing Excellence"
                className="w-full h-64 sm:h-[480px] object-cover"
              />
            </div>
            <div className="space-y-5 sm:space-y-6">
              <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase">
                About Us
              </p>
              <h2 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 leading-tight">
                SynergyTuff India
              </h2>
              <p className="font-roboto text-slate-600 leading-relaxed text-sm sm:text-base">
                Based in Jaipur, SynergyTuff India Private Limited brings over five decades of
                unmatched expertise in toughened and architectural glass business. Our company is
                committed to providing superior glass solutions that offer exceptional durability,
                strength, and safety. We offer a wide range of toughened glass options to suit your
                specific needs and requirements.
              </p>
              <Link
                to="/about"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 bg-brand hover:bg-brand-dark text-white font-rajdhani font-semibold px-6 sm:px-7 py-3 sm:py-3.5 text-sm sm:text-base transition-colors"
              >
                ABOUT US
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { value: '50+', label: 'Years Experience' },
              { value: '5000+', label: 'm\u00B2 Daily Capacity' },
              { value: '100%', label: 'Quality Inspection' },
              { value: 'ISO', label: 'Certified' },
            ].map((stat, index) => (
              <div key={index} className="text-center py-4 sm:py-6">
                <div className="font-rajdhani text-3xl sm:text-4xl md:text-5xl font-bold text-brand">{stat.value}</div>
                <div className="font-roboto text-xs sm:text-sm text-slate-500 mt-1 sm:mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
              Our Products
            </p>
            <h2 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
              Engineered For Excellence
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {products.slice(0, 4).map((product) => (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className="group overflow-hidden bg-white border border-slate-200 hover:border-brand/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5 sm:p-7">
                  <h3 className="font-rajdhani text-xl sm:text-2xl font-bold text-slate-800 mb-2 group-hover:text-brand transition-colors">
                    {product.name}
                  </h3>
                  <p className="font-roboto text-slate-500 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-5">
                    {product.description}
                  </p>
                  <Link
                    to="/products"
                    className="inline-flex items-center gap-2 font-rajdhani text-brand font-semibold text-sm hover:gap-3 transition-all"
                  >
                    View Specifications
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-14">
            <Link
              to="/products"
              data-testid="view-all-products"
              className="inline-flex items-center gap-2 border-2 border-brand text-brand hover:bg-brand hover:text-white font-rajdhani font-semibold px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base transition-all"
            >
              VIEW ALL PRODUCTS
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Applications Preview */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
              Applications
            </p>
            <h2 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
              Transforming Spaces Across Industries
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {applications.slice(0, 6).map((app) => (
              <div
                key={app.id}
                className="relative group overflow-hidden aspect-[4/3]"
              >
                <img
                  src={app.image}
                  alt={app.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent"></div>
                <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-end">
                  <h3 className="font-rajdhani text-lg sm:text-xl font-bold text-white mb-1 group-hover:text-brand-100 transition-colors">
                    {app.name}
                  </h3>
                  <p className="font-roboto text-white/70 text-xs sm:text-sm line-clamp-2">
                    {app.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-14">
            <Link
              to="/applications"
              data-testid="view-all-applications"
              className="inline-flex items-center gap-2 border-2 border-brand text-brand hover:bg-brand hover:text-white font-rajdhani font-semibold px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base transition-all"
            >
              VIEW ALL APPLICATIONS
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <p className="font-rajdhani text-brand font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase mb-2 sm:mb-3">
              Why Us
            </p>
            <h2 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
              Why Choose SynergyTuff?
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: Building2, title: "50+ Years Legacy", desc: "Half a century of glass manufacturing expertise passed through three generations" },
              { icon: Shield, title: "Premium Safety", desc: "Exceeding international safety standards with ISO certified manufacturing" },
              { icon: Sparkles, title: "Innovation Driven", desc: "Cutting-edge manufacturing technology with computer-controlled precision" },
              { icon: Award, title: "Quality Certified", desc: "Multi-stage quality inspection ensuring zero-compromise standards" }
            ].map((item, index) => (
              <div
                key={index}
                className="text-center p-4 sm:p-8 border border-slate-100 hover:border-brand/20 hover:shadow-md transition-all duration-300 group"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-5 flex items-center justify-center bg-brand-50 text-brand group-hover:bg-brand group-hover:text-white transition-all duration-300 rounded-sm">
                  <item.icon size={24} className="sm:hidden" />
                  <item.icon size={28} className="hidden sm:block" />
                </div>
                <h3 className="font-rajdhani text-sm sm:text-lg font-bold text-slate-800 mb-1 sm:mb-2">{item.title}</h3>
                <p className="font-roboto text-slate-500 text-xs sm:text-sm leading-relaxed hidden sm:block">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-brand relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1690944926899-cc0a52be0730"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 text-center">
          <h2 className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 sm:mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="font-roboto text-white/80 text-sm sm:text-lg max-w-2xl mx-auto mb-8 sm:mb-10">
            Let's discuss your architectural glass requirements. Our team of experts
            is ready to deliver solutions that exceed expectations.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-get-quote"
              className="inline-flex items-center justify-center gap-2 bg-white text-brand hover:bg-slate-100 font-rajdhani font-bold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 transition-colors"
            >
              GET YOUR QUOTE TODAY
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 font-rajdhani font-bold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 transition-colors"
            >
              VIEW OUR WORK
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
